/**
 * HOTFIX: Host-Aware Routing Context.
 *
 * Detects `window.location.hostname` and classifies the current host as:
 *   - "platform": app.dynicty.com or preview/dev host → render Owner Portal.
 *   - "tenant":   *.dynicty.com (non-reserved) subdomain OR a custom domain
 *                 (any non-platform FQDN) → render Tenant Storefront.
 *                 Actual tenant identity is resolved server-side via
 *                 /api/public/tenant-by-host (subdomain slug ATAU binding
 *                 tenant_domains ACTIVE untuk custom domain).
 *   - "unknown":  malformed / non-FQDN input (single label, empty, IP-ish
 *                 tanpa TLD huruf) → NotFound tanpa hit backend.
 *
 * Uses existing `/api/public/tenant-by-host` endpoint (zero new engine).
 * Kept small — pure context; no business logic.
 *
 * CUSTOM DOMAIN FIX (kurakurafishing.com case): sebelumnya host apa pun tidak
 * berakhiran `.dynicty.com` diklasifikasi `unknown` sehingga backend resolver
 * (yang SUDAH mendukung binding custom domain) tidak pernah dipanggil dari
 * SPA. Sekarang non-platform FQDN dirutekan lewat pipeline resolver yang
 * sama — cross-tenant isolation & fail-closed di backend tetap otoritatif.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { classifyHost, isDevHost, PLATFORM_APEX, PLATFORM_HOST } from "@/context/hostClassifier";

const HostContext = createContext({
  kind: "platform",
  host: "",
  tenant: null,
  loading: true,
  error: null,
});

// DEV-ONLY tenant simulation: pada preview/localhost, `?__tenant=<slug>` (atau
// localStorage `dynicty_dev_tenant`) mensimulasikan tenant host untuk E2E testing.
// TIDAK berlaku di production host (app.dynicty.com / *.dynicty.com).
// `?__tenant=off` menghapus simulasi.
function resolveDevTenantOverride(host) {
  if (typeof window === "undefined" || !isDevHost(host)) return null;
  try {
    const qp = new URLSearchParams(window.location.search).get("__tenant");
    if (qp === "off") {
      window.localStorage.removeItem("dynicty_dev_tenant");
      return null;
    }
    if (qp) {
      window.localStorage.setItem("dynicty_dev_tenant", qp);
      return qp;
    }
    return window.localStorage.getItem("dynicty_dev_tenant");
  } catch {
    return null;
  }
}

export function HostProvider({ children }) {
  const realHost = typeof window !== "undefined" ? window.location.hostname : "";
  const devTenant = resolveDevTenantOverride(realHost);
  const host = devTenant ? `${devTenant}.${PLATFORM_APEX}` : realHost;
  const classified = devTenant
    ? { kind: "tenant", slug: devTenant, customDomain: false }
    : classifyHost(realHost);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(classified.kind === "tenant");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (classified.kind !== "tenant") {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get("/public/tenant-by-host", { params: { host } });
        if (!cancelled) setTenant(r.data);
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          setError(status === 404 ? "not-found" : "network");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classified.kind, host]);

  return (
    <HostContext.Provider
      value={{
        kind: classified.kind,
        slug: classified.slug || null,
        customDomain: !!classified.customDomain,
        host,
        tenant,
        loading,
        error,
        platformApex: PLATFORM_APEX,
        platformHost: PLATFORM_HOST,
      }}
    >
      {children}
    </HostContext.Provider>
  );
}

export function useHost() {
  return useContext(HostContext);
}

export { classifyHost };
